import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

import * as Sentry from '@sentry/react';
import { useQuery, useQueryClient } from 'react-query';

import { useAuthentication } from '@hooks';

import SplashScreen from '@components/SplashScreen';

import { useApi } from './Api';

interface AuthContextValues {
  userInfo?: UserInfo;
  authorized: boolean;
  saveUserInfo: (user: UserInfo) => void;
  saveTokens: (tokens: Tokens) => void;
  logout: () => void;
  updateUserInfo: () => void;
}

const AuthContext = createContext<AuthContextValues>({} as AuthContextValues);

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isLoadingInitialValues, setIsLoadingInitialValues] = useState(true);
  const [accessToken, setAccessToken] = useState<string>();

  const { getProfile } = useAuthentication();
  const { addInterceptors, cleanInterceptors } = useApi();
  const queryClient = useQueryClient();

  const fetchUserInfo = useQuery('userInfo', getProfile, {
    onSuccess: (data) => {
      Sentry.setUser({
        id: data.id.toString(),
        email: data.email,
      });
    },
    onError: () => {
      localStorage.removeItem('tokens');
    },
    onSettled: () => setIsLoadingInitialValues(false),
    staleTime: Infinity,
    enabled: !!accessToken,
  });

  const logout = useCallback(async () => {
    cleanInterceptors();

    localStorage.removeItem('tokens');
    setAccessToken(undefined);

    queryClient.clear();
    Sentry.setUser(null);
  }, [cleanInterceptors, queryClient]);

  const saveUserInfo = useCallback(
    (user: UserInfo) => {
      queryClient.setQueryData('userInfo', user);
    },
    [queryClient]
  );

  const saveTokens = useCallback(
    (tokens: Tokens) => {
      cleanInterceptors();

      localStorage.setItem('tokens', JSON.stringify(tokens));

      setAccessToken(tokens.access);
      addInterceptors(tokens, logout);
    },
    [addInterceptors, cleanInterceptors, logout]
  );

  useEffect(() => {
    if (accessToken) {
      return;
    }

    const tokens = localStorage.getItem('tokens');

    if (tokens) {
      const parsedTokens: Tokens = JSON.parse(tokens);

      addInterceptors(parsedTokens, logout);

      setAccessToken(parsedTokens.access);
    } else {
      setIsLoadingInitialValues(false);
    }
  }, [accessToken, addInterceptors, logout]);

  return (
    <AuthContext.Provider
      value={{
        userInfo: queryClient.getQueryData('userInfo'),
        authorized: !!fetchUserInfo.data,
        updateUserInfo: fetchUserInfo.refetch,
        saveUserInfo,
        saveTokens,
        logout,
      }}
    >
      {isLoadingInitialValues ? <SplashScreen /> : children}
    </AuthContext.Provider>
  );
};

export function useAuth() {
  const context = useContext(AuthContext);
  return context;
}
